/**
 * Button Component
 * @module views/Atoms/Button
 */
import React, { useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import './Button.scss';
import Icon from '../Icon/Icon';
import datacapture from '../../../utils/datacapture/datacapture';
import { constants } from '../../../utils/constants/constants';
import { processClientActionType } from '../../../utils/clientActionProcessor/clientActionProcessor';

import { useOverlay } from '../../__core/OverlayProvider/OverlayProvider';

/**
 * A click handler for a the disabled button
 * @method
 * @param {Object} event
 * @returns false
 */
export const disabledHandler = ( event ) => {
  event.preventDefault();
  return false;
};
export const handleOnClick = ( props ) => {
  return function( e ){
    const dataCaptureData = props?.action?.dataCaptureData ;
    const clientActionType = props.action?.clientActionType;
    if( dataCaptureData ){
      datacapture.processEvents( { dataCapture : { ...dataCaptureData } }, constants.DATACAPTURE_EVENT_TYPE.click )
    }
    if( props.onClick ){
      props.onClick( e )
    }
    if( clientActionType ){
      processClientActionType( props.action )
    }
  }
};


/**
 * Represents a Button component
 * @method
 * @param {Object} props - React properties passed from composition
 * @param {Object} ref - React forwardRef passed to the button for parent control if needed
 * @returns Button
 */
const Button = forwardRef( ( props, ref ) => {
  const { openOverlay, isOverlay } = useOverlay();
  const buttonRef = useRef();
  const displayOverlay = props.action && isOverlay && isOverlay( props.action.navigationType );
  return (
    <button
      { ...( props.disabled && { disabled: true } ) }
      onClick={ props.disabled ? disabledHandler : Button.handleOnClick( props ) }
      onMouseDown={ props.disabled ? disabledHandler : props.onMouseDown }
      { ...( !isNaN( props.dataIndex ) && { 'data-index': props.dataIndex } ) }
      { ...( props.ariaLabel && { 'aria-label': props.ariaLabel } ) }
      { ...( props.ariaCurrent && { 'aria-current': props.ariaCurrent } ) }
      { ...( props.ariaControls && { 'aria-controls': props.ariaControls } ) }
      className={
        classNames( {
          ...( props.unstyled && {
            'Button--unstyled': true,
            [props.className]: props.className
          } ),
          ...( !props.unstyled && {
            'Button-ds': true,
            [props.className]: props.className,
            'Button-ds--clear': props.clear,
            'Button-ds--compact': props.compact,
            'Button-ds--tiny': props.tiny && props.tertiary,
            'Button-ds--disabled': props.disabled,
            'Button-ds--withHover': !props.disabled && props.withHover,
            'Button-ds--icon': props.icon,
            'Button-ds--icon--withHiddenLabel': !props.disabled && props.hiddenLabel,
            'Button-ds--fullWidth': props.fullWidth,
            'Button-ds--secondary': props.secondary,
            'Button-ds--tertiary': props.tertiary,
            'Link_Huge Button-ds--link': props.likeLink
          } )
        } )
      }

      ref={ buttonRef }
      type={ props.type }
      aria-expanded={ props.isExpanded }
      { ...( displayOverlay && { onClick: ( e ) => {
        handleOnClick( props )( e );
        openOverlay( {
          type: props.action.navigationType,
          opener: buttonRef,
          alignment: props.alignment,
          ...( !props.overlayContent && props.action.graphql && { graphql: props.action.graphql } ),
          ...( !props.overlayContent && props.action.config && { config: props.action.config } ),
          ...( props.overlayContent && { content: props.overlayContent } ),
          ...( props.isSwatchModal && { isSwatchModal: props.isSwatchModal } ),
          ...( props.crossButtonVisibility && { crossButtonVisibility: props.crossButtonVisibility } )
        } );
      } } ) }
    >
      { props.iconImage && !props.iconRight &&
        <Icon className={
          classNames( 'Button-ds__icon', {
            'Button-ds__icon--withIcon': !props.icon
          } ) }
        size={ props.iconSize }
        name={ props.iconImage }
        title={ props.iconLabel }
        { ...( props.ariaHiddenIcon && { 'aria-hidden': props.ariaHiddenIcon } ) }
        />
      }
      { !props.hiddenLabel && props.label &&
        props.label
      }
      { !props.compact && !props.disabled && props.hiddenLabel &&
        <span className='Button-ds__span--hidden-icon-label'>{ props.hiddenLabel }</span>
      }
      { props.iconImage && props.iconRight &&
        <Icon className={
          classNames( 'Button-ds__icon', {
            'Button-ds__icon--withIconRight': !props.icon
          } ) }
        size={ props.iconSize }
        name={ props.iconImage }
        title={ props.iconLabel }
        { ...( props.ariaHiddenIcon && { 'aria-hidden': props.ariaHiddenIcon } ) }
        />
      }
      { props.unstyled && props.children }
    </button>
  );
} );

Button.displayName = 'Button';

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} ariaLabel='' - Default ariaLabel
 * @property {string} className='' - Default className
 * @property {boolean} clear=false - Default clear variation
 * @property {boolean} compact=false - Default compact variation
 * @property {boolean} disabled=false - Default disabled variation
 * @property {boolean} hasArrow=false - Default hasArrow
 * @property {boolean} withHover=false - default withHover
 * @property {boolean} icon=false - Default icon variation
 * @property {string} iconImage='' - Default iconImage
 * @property {string} iconLabel='' - Default iconLabel
 * @property {boolean} iconRight=false - Default iconRight
 * @property {string} iconSize='s' - Default iconSize
 * @property {boolean} likeLink=false - Default likeLink
 * @property {function} onClick=null - Default onClick
 * @property {boolean} secondary=false - Default secondary variation
 * @property {boolean} tertiary=false - Default tertiary variation
 * @property {string} type='button' - Default type
 * @property {string} withHiddenLabel=null - Default withHiddenLabel
 * @property {function} onMouseDown=null - Default onMouseDown
 * @property {boolean} fullWidth=false - Default fullWidth variation
 * @property {boolean} isSwatchModal=false - Default isSwatchModal
 * @property {boolean} crossButtonVisibility=false - Default crossButtonVisibility
 * @property {string} alignment=center - Default alignment
 * @property {boolean} ariaHiddenIcon - sets the aria hidden
 */
export const defaultProps = {
  ariaLabel: '',
  className: '',
  clear: false,
  compact: false,
  disabled: false,
  hasArrow: false,
  withHover: false,
  icon: false,
  iconImage: null,
  iconLabel: null,
  iconRight: false,
  iconSize: 's',
  likeLink: false,
  onClick: null,
  secondary: false,
  tertiary: false,
  type: 'button',
  fullWidth: false,
  withHiddenLabel: null,
  onMouseDown: null,
  unstyled:false,
  isSwatchModal: false,
  crossButtonVisibility: false,
  alignment:'center',
  ariaHiddenIcon: false
};

/**
  * property type definitions
  * @type {object}
  * @property {string} ariaLabel - Text for the screen readers
  * @property {string} ariaControls - aria-control for ADA perspective
  * @property {string} className - Custom CSS classes names to apply
  * @property {boolean} clear - Flag to render the Icon circle with clear variation
  * @property {boolean} compact - Flag to render the compact variation
  * @property {boolean} tiny - Flag to render the tiny variation
  * @property {boolean} disabled - Flag to render the disabled variation
  * @property {boolean} withHover - Flag to render the withHover variation
  * @property {boolean} icon - Flag to render the icon only variation
  * @property {string} iconImage - Name of the Icon to render, renders the Icon
  * @property {string} iconLabel - Name of icon used for accessibility
  * @property {boolean} iconRight - Flag to render the icon to the right of the label
  * @property {string} iconSize - Size of the Icon to render, it's value matches the viewports naming: s, m, l
  * @property {boolean} likeLink - Flag to make the button have the styles of a Link
  * @property {string} label - Button text to render as child
  * @property {function} onClick - Function as the event handler
  * @property {boolean} secondary - Flag to render the Outline button
  * @property {boolean} tertiary - Flag to render the tertiary button
  * @property {boolean} fullWidth - Flag to render the full width button
  * @property {string} type - Type of button
  * @property {string} withHiddenLabel - Name of the Icon to render, renders the Icon + Label variation
  * @property {function} onMouseDown - Function as the event handler for mouse down
  * @property {boolean} unstyled - Flag to render the unstyled button
  * @property {boolean} isSwatchModal - Flag to set class for swatch details
  * @property {boolean} crossButtonVisibility - Flag to set class for modal/flyout x button
  * @property {string} alignment - Alignment for overlay modal/flyout
  * @property {object} overlayContent - content for overlay modal/flyout
  * @property {object} action - Set the label and graphql
  * @property {boolean} ariaHiddenIcon - Flag to announce the Icon for accessibility
*/
export const propTypes = {
  /** Text for the screen readers */
  ariaLabel: PropTypes.string,
  /** id to control the aria , for ADA https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-controls */
  ariaControls: PropTypes.string,
  /** Custom CSS class names to apply */
  className: PropTypes.string,
  /** Flag to render the Icon circle with clear variation */
  clear: PropTypes.bool,
  /** Flag to render the compact variation */
  compact: PropTypes.bool,
  /** Flag to render the tiny variation */
  tiny: PropTypes.bool,
  /** Flag to render the disabled variation */
  disabled: PropTypes.bool,
  /** Flag to render the withHover variation */
  withHover: PropTypes.bool,
  /** Flag to render the icon only variation */
  icon: PropTypes.bool,
  /** Name of the Icon to render, renders the Icon */
  iconImage: PropTypes.string,
  /** Name of icon used for accessibility */
  iconLabel: PropTypes.string,
  /** Flag to render the icon to the right of the label */
  iconRight: PropTypes.bool,
  /** Size of the Icon to render, it's value matches the viewports naming: s, m, l */
  iconSize: PropTypes.string,
  /** Flag to make the button have the styles of a Link */
  likeLink: PropTypes.bool,
  /** Text to render as child */
  label: PropTypes.string,
  /** Function as the event handler */
  onClick: PropTypes.func,
  /** Flag to render the secondary button */
  secondary: PropTypes.bool,
  /** Flag to render the tertiary button */
  tertiary: PropTypes.bool,
  /** Type of button */
  type: PropTypes.string,
  /** Name of the Icon to render, renders the Icon + Label variation */
  withHiddenLabel: PropTypes.string,
  /** Function as the event handler for mouse down*/
  onMouseDown: PropTypes.func,
  /** Flag to decide rendering of unstyled button */
  unstyled: PropTypes.bool,
  /** Flag to decide rendering of fullWidth button */
  fullWidth: PropTypes.bool,
  /** Flag to set class for swatch modal */
  isSwatchModal: PropTypes.bool,
  /** Flag to set class for modal/flyout x button */
  crossButtonVisibility: PropTypes.bool,
  /** Alignment for overlay modal/flyout */
  alignment: PropTypes.string,
  /** Content for overlay modal/flyout */
  overlayContent: PropTypes.object,
  /** get actions like label, graphql, navigationType */
  action: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string,
    dataCaptureData: PropTypes.object,
    clientActionType: PropTypes.object,
    navigationType: PropTypes.oneOfType( [PropTypes.object, PropTypes.string] )
  } ),
  /** Flag To Announce the Icon for accessibility */
  ariaHiddenIcon: PropTypes.bool
}

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;
Button.handleOnClick = handleOnClick ;

export default Button;
